import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { organizationService } from '../services/OrganizationService';

// One /api/organization/info lookup per signed-in profile, shared by every caller.
const cache = new Map<string, Promise<boolean>>();

/**
 * Whether the effective user is an admin of their organization (global admins
 * count too). `null` while loading. Used to hide org-admin-only surfaces such
 * as Usage & Plan; the backend enforces the same rule.
 */
export function useIsOrgAdmin(): boolean | null {
  const { currentUser, isImpersonating, impersonatedUser } = useAuth();
  const user = isImpersonating && impersonatedUser ? impersonatedUser : currentUser;
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);

  useEffect(() => {
    if (!user) { setIsAdmin(false); return; }
    if (user.role === 'admin' && !isImpersonating) { setIsAdmin(true); return; }
    if (!user.organization_id) { setIsAdmin(false); return; }
    const key = `${user.uid}:${user.organization_id}`;
    if (!cache.has(key)) {
      cache.set(
        key,
        organizationService
          .getOrganizationInfo()
          .then(res => !!res.data?.organization?.is_admin)
          .catch(() => { cache.delete(key); return false; }),
      );
    }
    let cancelled = false;
    cache.get(key)!.then(v => { if (!cancelled) setIsAdmin(v); });
    return () => { cancelled = true; };
  }, [user?.uid, user?.organization_id, user?.role, isImpersonating]); // eslint-disable-line react-hooks/exhaustive-deps

  return isAdmin;
}
